import { createFileRoute, notFound } from '@tanstack/react-router';
import { loadCatalogDetail } from '@/features/catalog/queries';
import { loadPageMeasuredFigures } from '@/features/usage/pageQueries';
import { platformPricePhrase } from '@/lib/seo/apiPrice';
import { platformPriceTitle, platformTitle } from '@/lib/seo/apiTitle';
import { localizedLinks, localizedUrl } from '@/i18n/localeHead';
import { m } from '@/paraglide/messages';
import { ApiDetailPage } from '../../../../pages/ApiDetailPage';

export const Route = createFileRoute('/_site/api/$apiId/')({
  staticData: { footer: 'slim', catalogText: true },
  // Resolve the platform detail in the loader so TanStack Start renders the
  // header (H1), blurb, and endpoint list server-side - the page is then
  // crawlable instead of shipping a client-only "Loading…" shell.
  //
  // The measured figures load here for the same reason. Left to a client
  // `useQuery` they reached humans and nobody else: per-endpoint uptime,
  // latency, and request volume are the one thing on this page that exists
  // nowhere else on the web, and no AI crawler runs the JavaScript that fetched
  // them, so the served HTML said "-" where the browser said "96.34%".
  loader: async ({ context, params }) => {
    const [{ detail, icon }] = await Promise.all([
      loadCatalogDetail(context.queryClient, params.apiId, 'platform'),
      loadPageMeasuredFigures(context.queryClient, { platform: params.apiId }),
    ]);
    // An unknown platform is a 404, not a 200 with a "not found" body. The
    // sitemap is generated live from the gateway catalog, so a retired SKU
    // drops out of it - without this throw the URL keeps returning 200 with a
    // self-referencing canonical and Google has no signal to deindex it.
    if (!detail) throw notFound();
    return { detail, icon };
  },
  head: ({ params, loaderData }) => {
    const path = `/api/${params.apiId}`;
    // A page that does not exist has no language versions to announce.
    const missing = { links: [{ rel: 'canonical', href: localizedUrl(path) }] };
    const detail = loaderData?.detail;
    if (!detail) return missing;
    // The price question is the winnable one, so a priced platform leads with its
    // figure and an unpriced one keeps the descriptive title.
    const phrase = platformPricePhrase(detail);
    const title = phrase
      ? platformPriceTitle(params.apiId, detail.name, phrase)
      : platformTitle(detail.name, detail.endpoints.map((e) => e.action));
    // The blurb is not appended: it ends on "per-request USD pricing and no
    // subscriptions", which this sentence has already said with a figure attached,
    // and a description Google truncates at ~155 characters cannot afford the repeat.
    const description = phrase
      ? m.head_api_price_description({ name: detail.name, phrase })
      : detail.blurb;
    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: localizedUrl(path) },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
      ],
      links: localizedLinks(path),
    };
  },
  component: ApiDetailRoute,
});

function ApiDetailRoute() {
  const { apiId } = Route.useParams();
  const { detail, icon } = Route.useLoaderData();
  return <ApiDetailPage apiId={apiId} detail={detail} icon={icon} presentation="site" />;
}
